import React from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store';
import { creditCostFor, fetchCreditsPricing } from '@/store/slices/aiCredits';
import { fetchOwnerWallets } from '@/store/slices/wallet';

import { Button, Callout, EmptyState, Modal, Select, Skeleton, Toast } from '../../components';
import { languageName } from '../../config/languageEndonyms';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useToast } from '../../hooks/useToast';
import { MOBILE } from '../../theme/tokens';
import { creativesServiceConfigured } from '../creatives/creative-api';
import { BuyCreditsSheet } from '../creatives/components/BuyCreditsSheet';
import { WebsitePage } from '../website/components/WebsiteShell';
import { useBrandSettled } from '../website/useBrandSettled';
import { AddRouteDialog } from './components/AddRouteDialog';
import { AdvancedPanel } from './components/AdvancedPanel';
import { FooterPanel } from './components/FooterPanel';
import { GenerateDefaults } from './components/GenerateDefaults';
import { GenerateHost, GenerateWithLumio } from './components/GenerateWithLumio';
import { KeywordsPanel } from './components/KeywordsPanel';
import { MobileActionBar } from './components/MobileActionBar';
import { RouteEditor } from './components/RouteEditor';
import { RouteList } from './components/RouteList';
import { SearchResultPanel } from './components/SearchResultPanel';
import { SeoHeaderActions } from './components/SeoHeaderActions';
import { SharePanel } from './components/SharePanel';
import { SiteDefaultsPanel } from './components/SiteDefaultsPanel';
import { defaultsPlan, defaultsRequest, hasDefaultsWork, titleTemplateToSet } from './defaults-plan';
import {
  generatedFieldsAt,
  generateRequestFor,
  markGenerated,
  SEO_PRICE_KEY,
  type GeneratedMarks,
  type GenerateTarget,
} from './generate-request';
import { hiddenBlockingIssues, issuesFor, orphanLanguageIssues, pathsWithErrors } from './issues-by-field';
import { keywordScores } from './keyword-checks';
import { publishSummary } from './publish-summary';
import { routeName } from './route-tree';
import { issueFieldCopy, publishSummaryCopy } from './seo-copy';
import {
  addCustomRoute,
  changedRoutes,
  orphanSitePages,
  removeRoute,
  routeOf,
  routesMissingLanguage,
  setSiteText,
} from './seo-document';
import type { SiteSeoDocument, SiteSeoGenerateField, SiteSeoJob, SiteSeoRoute } from './seo-types';
import { blocksPublish, PLACEHOLDER_RE } from './seo-validation';
import {
  clearedSelection,
  routeChosen,
  searchWithoutRoute,
  searchWithSelection,
  selectionFromSearch,
  SITE_DEFAULTS,
  type SeoSelection,
} from './selection';
import { getSiteSeoUrls } from './site-seo-api';
import { isJobOver } from './site-seo-state';
import { useSiteSeo, type SiteSeoGenerateResult } from './useSiteSeo';
import {
  Actions,
  ConfirmBody,
  ConfirmLine,
  ConfirmNote,
  EditorColumn,
  Failure,
  Layout,
  MobileEditorHead,
  MobileLanguage,
  Page,
  SkeletonStack,
} from './seo.styles';

export type PageSeoProps = {
  styleText?: string;
  className?: string;
};

const NO_LANGUAGES = { default: '', all: [] as string[] };

type Confirm = 'publish' | 'undo' | null;

/** The job seen on the last render, with the instance it ran for: a job that ends shows its outcome once. */
type SeenJob = { casinoInstanceId: string | undefined; running: SiteSeoJob | null };

/** A panel Lumio writes into. */
type Host = 'search' | 'footer' | 'ai';

/** The panel Lumio is writing into, and the route it writes for (null = the site); `'defaults'` is the site defaults
 *  bundle, which writes into every site-level panel. */
type Generating = { host: Host | 'defaults'; path: string | null };

/** The panel a target writes into. */
const hostOf = (target: GenerateTarget): Host => (target === 'search' ? 'search' : target === 'ai' ? 'ai' : 'footer');

/** The generated fields each panel shows (a site-level mark of one panel's field is not another panel's). */
const HOST_FIELDS: Record<Host, readonly SiteSeoGenerateField[]> = {
  search: ['title', 'description'],
  footer: ['footer_text'],
  ai: ['llms_summary', 'llms_facts'],
};

/** How the site defaults bundle went, language by language, for its one toast. */
type DefaultsOutcome = {
  /** Languages where something landed. */
  written: string[];
  /** Languages whose call failed on the server's side (refunded there) or never reached it. */
  failed: string[];
  /** Languages where nothing landed because the owner wrote the asked fields during the call (charged). */
  kept: string[];
  /** A landed text holds a `[bracketed]` placeholder. */
  placeholder: boolean;
};

const names = (languages: string[]): string => languages.map(languageName).join(', ');

/** The bundle's toast: what was written, what failed, what the owner's own text kept, and the placeholder note. */
const defaultsOutcomeCopy = (t: TFunction, o: DefaultsOutcome): string => {
  const parts: string[] = [];
  if (o.failed.length === 0 && o.kept.length === 0) {
    if (o.written.length === 1) parts.push(t('Site defaults written for 1 language.'));
    else if (o.written.length > 1) parts.push(t('Site defaults written for {{n}} languages.', { n: o.written.length }));
  } else if (o.written.length > 0) {
    parts.push(t('Written for {{written}}.', { written: names(o.written) }));
  }
  if (o.failed.length > 0) parts.push(t("Couldn't write {{failed}} (not charged).", { failed: names(o.failed) }));
  if (o.kept.length > 0) {
    parts.push(t('Kept what you wrote for {{languages}} while Lumio was writing.', { languages: names(o.kept) }));
  }
  if (o.placeholder) parts.push(t('Check the [bracketed] placeholders before publishing.'));
  return parts.join(' ');
};

/** The buy credits sheet: closed, or open with the shortfall a 402 reported (none from the pre-flight gate). */
type Buy = { open: false } | { open: true; suggested?: number };

/**
 * Website → SEO (`/account/website/seo`), behind `website-seo`: the owner's meta and footer text per route, for the
 * selected brand's site. The selection (`?route=`, `?lang=`) lives in the URL; data, autosave and the publish job in
 * {@link useSiteSeo}. This file wires the header actions, the route list and the selected route's editor with its
 * panels: Site defaults (with AI assistants) and the site's Footer SEO text, or a route's Search result, Focus keyword
 * (not on a footer wildcard), Social share, Footer SEO text and Advanced.
 */
const PageSeo = ({ styleText = '', className = '' }: PageSeoProps): React.ReactElement => {
  const { t } = useTranslation();
  const { toast, show } = useToast();
  const dispatch = useAppDispatch();
  const mobile = useMediaQuery(MOBILE);
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString();
  const casinoInstanceId = useAppSelector((state) => state.casinoInstance.displayBrand?.id) || undefined;
  // The brand's tenant: its website art is the share image library (none without a usable id).
  const tenantRaw = Number(useAppSelector((state) => state.casinoInstance.displayBrand?.tenant_id));
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const brandSettled = useBrandSettled();
  const seo = useSiteSeo({ casinoInstanceId });
  const { status, snapshot, openRoute, setDraft, reload, reloadFailed, saveFailed, confirmed, publishing } = seo;

  const [addOpen, setAddOpen] = React.useState(false);
  const [confirm, setConfirm] = React.useState<Confirm>(null);

  // Lumio generation: one credit charge per call, its price from the creatives price table. The cost reads on each
  // Generate control and backs a fail-open pre-flight (an unknown balance or price blocks nothing); the service's 402
  // is the authoritative refusal and opens the buy credits sheet with the shortfall.
  const pricing = useAppSelector((state) => state.aiCredits.pricing);
  const balance = useAppSelector((state) => state.ownerWallet.credits);
  const cost = creditCostFor(pricing, SEO_PRICE_KEY);
  const [generating, setGenerating] = React.useState<Generating | null>(null);
  // One generation at a time, held before the disabled controls re-render (a double click charges once).
  const generatingRef = React.useRef(false);
  const [marks, setMarks] = React.useState<GeneratedMarks>({});
  const [buy, setBuy] = React.useState<Buy>({ open: false });
  React.useEffect(() => {
    void dispatch(fetchCreditsPricing());
  }, [dispatch]);

  const state = snapshot?.state ?? null;
  const draft = snapshot?.draft ?? null;
  const languages = state?.languages ?? NO_LANGUAGES;
  const selection = selectionFromSearch(search, draft, languages);
  // The draft as last rendered, read between the awaits of the site defaults bundle (null once the snapshot is gone).
  const draftRef = React.useRef<SiteSeoDocument | null>(draft);
  draftRef.current = draft;
  const running = publishing && !isJobOver(publishing) ? publishing : null;

  // The route list's keyword scores, every route against every other: rebuilt only when what they read changes, not on
  // each render (before the early returns below, so the hook order never changes).
  const liveFooterRules = state?.live.footer_rules;
  const footerOverridable = state?.footer_overridable ?? false;
  const scores = React.useMemo(
    () =>
      draft && liveFooterRules
        ? keywordScores(draft, {
            language: selection.language,
            defaultLanguage: languages.default,
            liveFooterRules,
            footerOverridable,
          })
        : undefined,
    [draft, selection.language, languages.default, liveFooterRules, footerOverridable],
  );

  const select = React.useCallback(
    (next: SeoSelection): void =>
      setSearchParams((prev) => searchWithSelection(prev.toString(), next), { replace: true }),
    [setSearchParams],
  );

  // A brand switch opens the next brand on Site defaults (a first mount keeps a deep link's selection).
  const instanceRef = React.useRef(casinoInstanceId);
  React.useEffect(() => {
    const previous = instanceRef.current;
    instanceRef.current = casinoInstanceId;
    if (previous !== undefined && casinoInstanceId !== undefined && previous !== casinoInstanceId) {
      setSearchParams((prev) => clearedSelection(prev.toString()), { replace: true });
      setMarks({});
    }
  }, [casinoInstanceId, setSearchParams]);

  // Opening a route asks the service for its live meta (exact and pattern routes; the hook skips the rest).
  React.useEffect(() => {
    if (status === 'ready') openRoute(selection.path);
  }, [status, selection.path, openRoute]);

  // One toast per failed re-read: the last good state stays on screen.
  React.useEffect(() => {
    if (reloadFailed) show(t("Couldn't refresh. Your edits are kept."));
  }, [reloadFailed, show, t]);

  // A job that ends shows its outcome once, decided on its own final answer: its error when it failed, nothing when
  // the site may not carry it (the header's "Not on your site yet" says so), else the success toast. A job that ended
  // without an answer (it vanished, a re-read showed nothing running) shows only the re-read state. A brand switch is
  // not a job ending: the job is remembered only for the instance the hook has loaded.
  const seenRef = React.useRef<SeenJob>({ casinoInstanceId, running: null });
  const { endedJob, lastJobUnconfirmed } = seo;
  React.useEffect(() => {
    const seen = seenRef.current;
    const switched = seen.casinoInstanceId !== casinoInstanceId;
    seenRef.current = { casinoInstanceId, running: switched ? null : running };
    if (switched || seen.running === null || running !== null) return;
    if (!endedJob || endedJob.id !== seen.running.id) return;
    if (endedJob.status === 'failed') {
      show(
        endedJob.error ||
          (seen.running.action === 'revert'
            ? t("That couldn't be undone. Please try again.")
            : t("That couldn't be published. Please try again.")),
      );
      return;
    }
    if (lastJobUnconfirmed) return;
    show(seen.running.action === 'revert' ? t('Your last publish was undone.') : t('Published to your site.'));
  }, [casinoInstanceId, running, endedJob, lastJobUnconfirmed, show, t]);

  const runJob = async (kind: 'publish' | 'undo'): Promise<void> => {
    setConfirm(null);
    const res = kind === 'publish' ? await seo.publish() : await seo.revert();
    if (res.ok) return;
    // A refusal shows its message (the hook re-reads as well); anything else, a plain failure.
    if (res.reason === 'refused' && res.message) show(res.message);
    else if (kind === 'publish') show(t("That couldn't be published. Please try again."));
    else show(t("That couldn't be undone. Please try again."));
  };

  const runGenerate = async (target: GenerateTarget, route: SiteSeoRoute | null, language: string): Promise<void> => {
    if (generatingRef.current) return;
    if (balance != null && cost != null && balance < cost) {
      setBuy({ open: true });
      return;
    }
    const req = generateRequestFor(target, route, language);
    const instance = instanceRef.current;
    generatingRef.current = true;
    setGenerating({ host: hostOf(target), path: target === 'ai' ? null : route?.path ?? null });
    let res: SiteSeoGenerateResult;
    try {
      // Lumio reads the keywords from the stored draft: a pending edit (a keyword just typed) is saved first.
      await seo.flushDraft();
      if (instance !== instanceRef.current) return;
      res = await seo.generate(req);
    } finally {
      generatingRef.current = false;
      setGenerating(null);
    }
    // A brand switch while Lumio wrote: the answer was for the other site (the hook did not land it either).
    if (instance !== instanceRef.current) return;
    if (res.ok) {
      const generated = res.value;
      setMarks((m) => markGenerated(m, req, generated));
      void dispatch(fetchOwnerWallets());
      return;
    }
    if (res.reason === 'credits') {
      setBuy({ open: true, suggested: res.needed && res.needed > 0 ? res.needed : undefined });
      return;
    }
    // The service's own sentence when it gave one (a refusal, or a failure after the charge, refunded); else ours.
    const message = 'message' in res ? res.message : undefined;
    show(message || t("Lumio couldn't write that. Please try again."));
  };

  /**
   * Generate site defaults: one Lumio call per selected language (one charge each), the default language first, for
   * the site fields still empty in the live draft. Each language's draft is saved before the next call, since the
   * server adapts the other languages from the stored default text. A field typed into during a call keeps the
   * owner's text. An empty title template is set to `{page} | brand` by the page itself (no call, no charge, no
   * generated mark), checked against the live draft as it lands; a language with only that to fill makes no call.
   * Failures: no credits stops and opens the buy sheet; a refusal stops with the server's sentence; a server failure
   * (refunded) goes on with the next language; an unavailable service stops. One toast at the end.
   */
  const runGenerateDefaults = async (selected: string[]): Promise<void> => {
    if (generatingRef.current || !state) return;
    const overridable = state.footer_overridable;
    const brandName = state.site.brand_name;
    const planOf = (doc: SiteSeoDocument, language: string) =>
      defaultsPlan(doc, languages, overridable, brandName).languages.find((p) => p.language === language);
    const live = draftRef.current;
    if (!live) return;
    const order = [
      ...selected.filter((l) => l === languages.default),
      ...selected.filter((l) => l !== languages.default),
    ].filter((l) => {
      const plan = planOf(live, l);
      return plan !== undefined && hasDefaultsWork(plan);
    });
    if (order.length === 0) {
      show(t('Everything is already written. Clear a field to let Lumio write it.'));
      return;
    }
    const calls = order.filter((l) => (planOf(live, l)?.fields.length ?? 0) > 0).length;
    if (balance != null && cost != null && calls > 0 && balance < cost * calls) {
      setBuy({ open: true });
      return;
    }
    const instance = instanceRef.current;
    const switched = (): boolean => instance !== instanceRef.current || draftRef.current === null;
    const outcome: DefaultsOutcome = { written: [], failed: [], kept: [], placeholder: false };
    let stop: string | null = null;
    generatingRef.current = true;
    setGenerating({ host: 'defaults', path: null });
    try {
      for (const language of order) {
        // The previous language's text (and any pending edit) is stored before this call reads it.
        await seo.flushDraft();
        if (switched()) return;
        const now = draftRef.current;
        const plan = now ? planOf(now, language) : undefined;
        // Written meanwhile: no call, no charge.
        if (!plan || !hasDefaultsWork(plan)) continue;
        if (plan.titleTemplate !== null) {
          // Set by the page, not Lumio: only while the template is still empty in the draft it lands on.
          setDraft((d) => {
            const value = titleTemplateToSet(d, language, brandName);
            return value === null ? d : setSiteText(d, 'title_template', language, value);
          });
        }
        if (plan.fields.length === 0) {
          outcome.written.push(language);
          continue;
        }
        const req = defaultsRequest(plan);
        const res = await seo.generate(req, 'fill-empty');
        // A brand switch while Lumio wrote: the answer was for the other site (the hook did not land it either).
        if (switched()) return;
        if (res.ok) {
          setMarks((m) => markGenerated(m, req, res.value));
          outcome.written.push(language);
          if (Object.values(res.value).some((v) => v !== undefined && PLACEHOLDER_RE.test(v))) {
            outcome.placeholder = true;
          }
          continue;
        }
        if (res.reason === 'credits') {
          setBuy({ open: true, suggested: res.needed && res.needed > 0 ? res.needed : undefined });
          break;
        }
        if (res.reason === 'refused') {
          stop = res.message || t("Lumio couldn't write that. Please try again.");
          break;
        }
        if (res.reason === 'unavailable') {
          stop = t("Lumio couldn't write that. Please try again.");
          break;
        }
        // `kept`: the answer landed nothing, the owner wrote every asked field during the call (charged); anything
        // else is a failure on the server's side (refunded) or a call that never reached it.
        (res.reason === 'kept' ? outcome.kept : outcome.failed).push(language);
      }
    } finally {
      generatingRef.current = false;
      setGenerating(null);
    }
    void dispatch(fetchOwnerWallets());
    const message = [defaultsOutcomeCopy(t, outcome), stop ?? ''].filter((part) => part !== '').join(' ');
    if (message !== '') show(message);
  };

  const serviceOn = casinoInstanceId !== undefined && creativesServiceConfigured();
  const header = (body: React.ReactNode): React.ReactElement => (
    <WebsitePage styleText={styleText} className={className}>
      <Page>{body}</Page>
    </WebsitePage>
  );

  if (!brandSettled || (serviceOn && (status === 'idle' || status === 'loading'))) {
    return header(
      <SkeletonStack aria-busy="true">
        <Skeleton height={40} block />
        <Skeleton height={320} block />
      </SkeletonStack>,
    );
  }

  if (!serviceOn || status === 'unavailable') {
    return header(
      <>
        <SeoHeaderActions
          languages={NO_LANGUAGES}
          language=""
          onLanguage={() => undefined}
          dirty={false}
          publishing={null}
          confirmed
          canRevert={false}
          blocked={false}
          onPublish={() => undefined}
          onUndo={() => undefined}
        />
        <EmptyState text="SEO editing isn't available for this site yet" />
      </>,
    );
  }

  if (status === 'failed' || !snapshot || !state || !draft) {
    return header(
      <Failure>
        <Callout tone="danger" text="Couldn't load SEO for this site." />
        <Button label={t('Try again')} variant="outline" icon="refresh" onClick={reload} />
      </Failure>,
    );
  }

  const changed = changedRoutes(draft, state.published);
  const missingLanguage = new Set(routesMissingLanguage(draft, selection.language, languages.default));
  const orphans = new Set(orphanSitePages(draft, state.pages));
  const missingPages = new Set(
    draft.routes
      .filter((r) => issuesFor(snapshot.issues, r.path, 'path').some((i) => i.code === 'page-missing'))
      .map((r) => r.path),
  );
  const route = selection.path === SITE_DEFAULTS ? null : routeOf(draft, selection.path) ?? null;
  const page = state.pages.find((p) => p.path === selection.path);
  // Errors no panel of the open route shows (another language, another route, or no field): listed at page level, so a
  // blocked Publish always points at what blocks it.
  const errors = pathsWithErrors(snapshot.issues);
  const hidden = hiddenBlockingIssues(
    snapshot.issues,
    { path: route?.path ?? null, language: selection.language, match: route?.match },
    languages.all,
  );
  const pathError = route
    ? issuesFor(snapshot.issues, route.path, 'path').find((i) => i.level === 'error' && i.message !== '')?.message
    : undefined;
  const blocked = blocksPublish(snapshot.issues);
  const summary = publishSummary(draft, state.published);
  const generateControl = (host: Host, target: SiteSeoRoute | null): React.ReactElement => (
    <GenerateWithLumio
      target={host}
      cost={cost}
      busy={generating !== null && generating.host === host}
      disabled={generating !== null}
      onGenerate={(next) => void runGenerate(next, target, selection.language)}
    />
  );
  /** The panel `host` of `target` while Lumio writes into it, with the fields still holding what it wrote. */
  const hostProps = (host: Host, path: string | null) => ({
    busy:
      generating !== null && generating.path === path && (generating.host === host || generating.host === 'defaults'),
    generated: generatedFieldsAt(marks, draft, path, selection.language).filter((field) =>
      HOST_FIELDS[host].includes(field),
    ),
  });
  const footerPanel = (target: SiteSeoRoute | null): React.ReactElement => (
    <GenerateHost kind="footer" {...hostProps('footer', target?.path ?? null)}>
      <FooterPanel
        draft={draft}
        liveFooterRules={state.live.footer_rules}
        route={target}
        language={selection.language}
        defaultLanguage={languages.default}
        overridable={state.footer_overridable}
        reference={state.footer_reference}
        issues={snapshot.issues}
        onChange={setDraft}
        generateSlot={generateControl('footer', target)}
      />
    </GenerateHost>
  );

  // On a phone the page has two levels, driven by `?route=`: the route list, then the chosen route's editor (with a
  // back button and the language picker); the header actions move to a bar pinned at the bottom.
  const editorOpen = routeChosen(search);
  const showList = !mobile || !editorOpen;
  const showEditor = !mobile || editorOpen;

  const routeList = (
    <RouteList
      routes={draft.routes}
      pages={state.pages}
      selected={selection.path}
      language={selection.language}
      defaultLanguage={languages.default}
      changed={changed}
      missingLanguage={missingLanguage}
      orphans={orphans}
      missingPages={missingPages}
      errors={errors}
      scores={scores}
      onSelect={(path) => select({ path, language: selection.language })}
      onAdd={() => setAddOpen(true)}
    />
  );

  const editor = (
    <RouteEditor
      route={route}
      page={page}
      language={selection.language}
      defaultLanguage={languages.default}
      missingPage={route !== null && missingPages.has(route.path)}
      pathError={pathError}
      onDelete={
        route?.origin === 'custom'
          ? () => {
              setDraft((d) => removeRoute(d, route.path));
              select({ path: SITE_DEFAULTS, language: selection.language });
            }
          : undefined
      }
    >
      {route === null ? (
        <>
          <GenerateDefaults
            plan={defaultsPlan(draft, languages, state.footer_overridable, state.site.brand_name)}
            defaultLanguage={languages.default}
            cost={cost}
            busy={generating?.host === 'defaults'}
            disabled={generating !== null}
            onGenerate={(selected) => void runGenerateDefaults(selected)}
          />
          <SiteDefaultsPanel
            draft={draft}
            language={selection.language}
            defaultLanguage={languages.default}
            siteHost={state.site.host}
            issues={snapshot.issues}
            tenantId={tenantId}
            brandName={state.site.brand_name}
            llmsPreview={snapshot.llmsPreview}
            onChange={setDraft}
            aiGenerateSlot={generateControl('ai', null)}
            aiGenerating={hostProps('ai', null)}
            searchGenerating={hostProps('search', null)}
          />
          {footerPanel(null)}
        </>
      ) : (
        <React.Fragment key={route.path}>
          <GenerateHost kind="search" {...hostProps('search', route.path)}>
            <SearchResultPanel
              draft={draft}
              published={state.published}
              liveMeta={seo.liveMetaOf(route.path)}
              route={route}
              page={page}
              language={selection.language}
              defaultLanguage={languages.default}
              siteUrl={`https://${state.site.host}`}
              disabled={route.match === 'wildcard'}
              issues={snapshot.issues}
              onChange={setDraft}
              generateSlot={generateControl('search', route)}
            />
          </GenerateHost>
          {route.match !== 'wildcard' ? (
            <KeywordsPanel
              draft={draft}
              route={route}
              language={selection.language}
              defaultLanguage={languages.default}
              liveMeta={seo.liveMetaOf(route.path)}
              liveFooterRules={state.live.footer_rules}
              footerOverridable={state.footer_overridable}
              onChange={setDraft}
            />
          ) : null}
          <SharePanel
            draft={draft}
            route={route}
            liveMeta={seo.liveMetaOf(route.path)}
            language={selection.language}
            defaultLanguage={languages.default}
            siteUrl={`https://${state.site.host}`}
            tenantId={tenantId}
            brandName={state.site.brand_name}
            disabled={route.match === 'wildcard'}
            issues={snapshot.issues}
            onChange={setDraft}
          />
          {footerPanel(route)}
          <AdvancedPanel
            draft={draft}
            route={route}
            siteHost={state.site.host}
            disabled={route.match === 'wildcard'}
            issues={snapshot.issues}
            onChange={setDraft}
          />
        </React.Fragment>
      )}
    </RouteEditor>
  );

  return header(
    <>
      {!mobile ? (
        <SeoHeaderActions
          languages={languages}
          language={selection.language}
          onLanguage={(language) => select({ path: selection.path, language })}
          dirty={seo.dirty}
          publishing={running}
          confirmed={confirmed}
          canRevert={state.can_revert}
          blocked={blocked}
          blockedReason={t('Fix the fields marked in red first')}
          onPublish={() => setConfirm('publish')}
          onUndo={() => setConfirm('undo')}
        />
      ) : null}
      {saveFailed ? (
        <Callout
          tone="danger"
          text="Your last change couldn't be saved. It's still here, so you can try again."
          action={<Button label={t('Try again')} variant="outline" onClick={() => setDraft((d) => d)} />}
        />
      ) : null}
      {orphanLanguageIssues(snapshot.issues, languages.all).map((issue, index) => (
        <Callout
          key={`${issue.code}|${issue.language ?? ''}|${issue.path ?? ''}|${issue.field ?? ''}|${index}`}
          tone={issue.level === 'error' ? 'danger' : 'warning'}
          text="For {{language}}, which isn't one of your site's languages: {{message}}"
          placeholders={{ language: languageName(issue.language ?? ''), message: issue.message }}
        />
      ))}
      {hidden.map((issue, index) => {
        const site = issue.path === undefined || issue.path === '';
        const at = site ? null : routeOf(draft, issue.path ?? '');
        const where = [
          site
            ? t('Site defaults')
            : at
              ? routeName(
                  at,
                  state.pages.find((p) => p.path === at.path),
                  selection.language,
                  languages.default,
                  t('Homepage'),
                )
              : issue.path ?? '',
          issueFieldCopy(t, issue.field, site),
          issue.language ? languageName(issue.language) : '',
        ].filter((part) => part !== '');
        const target = site ? SITE_DEFAULTS : at?.path;
        return (
          <Callout
            key={`${issue.code}|${issue.language ?? ''}|${issue.path ?? ''}|${issue.field ?? ''}|${index}`}
            tone="danger"
            text="Can't publish yet. {{where}}: {{message}}"
            placeholders={{ where: where.join(' · '), message: issue.message }}
            action={
              target !== undefined ? (
                <Button
                  label={t('Show')}
                  variant="outline"
                  onClick={() => select({ path: target, language: issue.language || selection.language })}
                />
              ) : undefined
            }
          />
        );
      })}
      <Layout>
        {showList ? routeList : null}
        {showEditor ? (
          <EditorColumn>
            {mobile ? (
              <MobileEditorHead>
                <Button
                  label={t('Back to pages')}
                  variant="text"
                  icon="arrow-left"
                  onClick={() => setSearchParams((prev) => searchWithoutRoute(prev.toString()), { replace: true })}
                />
                {languages.all.length > 0 ? (
                  <MobileLanguage>
                    <Select
                      label={t('Language')}
                      hideLabel
                      value={selection.language}
                      onChange={(language) => select({ path: selection.path, language })}
                      options={languages.all.map((code) => ({ value: code, label: languageName(code) }))}
                    />
                  </MobileLanguage>
                ) : null}
              </MobileEditorHead>
            ) : null}
            {editor}
          </EditorColumn>
        ) : null}
      </Layout>
      {mobile ? (
        <MobileActionBar
          dirty={seo.dirty}
          publishing={running}
          confirmed={confirmed}
          canRevert={state.can_revert}
          blocked={blocked}
          onPublish={() => setConfirm('publish')}
          onUndo={() => setConfirm('undo')}
        />
      ) : null}
      <AddRouteDialog
        open={addOpen}
        existing={draft.routes.map((r) => r.path)}
        pages={state.pages}
        searchUrls={(q, signal) =>
          casinoInstanceId === undefined
            ? Promise.resolve({ ok: false, reason: 'unavailable' })
            : getSiteSeoUrls(casinoInstanceId, q, signal)
        }
        onClose={() => setAddOpen(false)}
        onAdd={(path) => {
          setAddOpen(false);
          setDraft((d) => addCustomRoute(d, path));
          select({ path, language: selection.language });
        }}
      />
      <Modal
        open={confirm === 'publish'}
        title={t('Publish to your site?')}
        onClose={() => setConfirm(null)}
        footer={
          <Actions>
            <Button label={t('Cancel')} variant="outline" onClick={() => setConfirm(null)} />
            <Button label={t('Publish')} onClick={() => void runJob('publish')} />
          </Actions>
        }
      >
        <ConfirmBody>
          <ConfirmLine text={publishSummaryCopy(t, summary.routes, summary.languages)} raw tag="paragraph" />
          <ConfirmNote
            text="Live on your site now. Shares and Google can take up to 24 hours to show it."
            tag="paragraph"
          />
        </ConfirmBody>
      </Modal>
      <Modal
        open={confirm === 'undo'}
        title={t('Undo your last publish?')}
        onClose={() => setConfirm(null)}
        footer={
          <Actions>
            <Button label={t('Cancel')} variant="outline" onClick={() => setConfirm(null)} />
            <Button label={t('Undo last publish')} onClick={() => void runJob('undo')} />
          </Actions>
        }
      >
        <ConfirmNote text="Your site goes back to what it showed before your last publish." tag="paragraph" />
      </Modal>
      {buy.open ? (
        <BuyCreditsSheet
          suggestedCredits={buy.suggested}
          onClose={() => setBuy({ open: false })}
          onSuccess={() => show(t('Credits added to your balance.'))}
        />
      ) : null}
      <Toast toast={toast} />
    </>,
  );
};

export default PageSeo;
